import axios from 'axios';
import { reportUploadFailure } from './uploadDiagnostics';
import {
  shouldUploadAsMultipart,
  uploadFileByMultipart,
} from './uploadFileByMultipart';

const DEFAULT_MAX_ATTEMPTS = 3;
const BASE_BACKOFF_MS = 1000;

// An expired signature is worth one more go, not three: if the link could not
// carry the file inside the signing window once, it usually cannot the next
// time either, and each further attempt costs the user the whole file again
// over mobile data.
const MAX_EXPIRED_SIGNATURE_RETRIES = 1;

const queryParam = (url, name) => {
  const match = new RegExp(`[?&]${name}=([^&]*)`).exec(url || '');
  return match ? decodeURIComponent(match[1]) : null;
};

/**
 * The instant a signed URL stops being accepted, in epoch ms, or null when the
 * URL carries no deadline we recognise.
 *
 * The API signs with SigV2, whose `Expires` is an absolute epoch second. SigV4
 * (`X-Amz-Date` plus a relative `X-Amz-Expires`) is read as well so this keeps
 * working if the signer is ever changed.
 */
export const signedUrlExpiryMs = (signedUrl) => {
  const v2 = queryParam(signedUrl, 'Expires');
  if (v2 && /^\d+$/.test(v2)) return Number(v2) * 1000;

  const date = queryParam(signedUrl, 'X-Amz-Date');
  const lifetime = queryParam(signedUrl, 'X-Amz-Expires');
  const parts =
    date && /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(date);
  if (parts && lifetime && /^\d+$/.test(lifetime)) {
    const [, y, mo, d, h, mi, s] = parts;
    const signedAt = Date.UTC(+y, +mo - 1, +d, +h, +mi, +s);
    return signedAt + Number(lifetime) * 1000;
  }

  return null;
};

/**
 * Whether this 403 is S3 rejecting the signature for having aged out.
 *
 * S3 says so in the response body, but that body is a cross-origin error and is
 * often not readable from here, so the deadline baked into the URL we signed is
 * the reliable half: once it has passed, an expired signature is the only thing
 * a 403 on a freshly signed upload URL can mean.
 */
const isExpiredSignature = (error, signedUrl) => {
  if (!error.response || error.response.status !== 403) return false;

  const body = error.response.data;
  if (typeof body === 'string' && body.indexOf('Request has expired') !== -1) {
    return true;
  }

  const expiresAt = signedUrlExpiryMs(signedUrl);
  return expiresAt !== null && Date.now() >= expiresAt;
};

/**
 * Whether another attempt could plausibly succeed.
 *
 * No response at all means the connection died partway through the body, which
 * on a phone on a building site is the ordinary case and exactly what retrying
 * is for. A 4xx is normally S3 refusing the request itself — a content-type
 * mismatch, a key the caller may not write — and refusing it again costs the
 * user another upload of the whole file for nothing.
 *
 * The exception is the 403 for an expired signature. That one is not a verdict
 * on the request, only on how long it took to arrive, and since every attempt
 * signs afresh (see below) it is precisely the failure a retry repairs.
 */
const isRetryable = (error, signedUrl, expiredSignatureRetries) => {
  if (axios.isCancel(error)) return false;
  if (!error || !error.response) return true;

  const status = error.response.status;
  if (status >= 400 && status < 500) {
    return (
      isExpiredSignature(error, signedUrl) &&
      expiredSignatureRetries < MAX_EXPIRED_SIGNATURE_RETRIES
    );
  }

  return true;
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * PUTs a file to S3, retrying when the connection drops.
 *
 * Takes `getSignedUrl` rather than a URL because upload URLs are signed for a
 * limited window: by the time a failed attempt has backed off, the URL it used
 * may well be past that, and reusing it would turn a retryable network blip
 * into a guaranteed 403. Each attempt therefore signs afresh.
 *
 * Throws `Error('Upload canceled')` when the caller's token is cancelled, which
 * is the signal callers already distinguish from a failure.
 *
 * Callers that pass `multipart` get a resumable upload in parts once the file
 * is big enough to need one; everything smaller keeps the single PUT below.
 */
export const uploadFileBySignedUrl = async ({
  file,
  getSignedUrl,
  cancelToken,
  onUploadProgress,
  multipart,
  maxAttempts = DEFAULT_MAX_ATTEMPTS,
  // Optional so the existing call sites keep working untouched. Only used to
  // say which project and which upload path a failure belongs to.
  context = {},
}) => {
  // A big file in one PUT has to hold a single signature across the whole
  // transfer and loses everything to one dropped connection. Parts carry their
  // own signature each, and an interruption costs a part.
  if (multipart && shouldUploadAsMultipart(file)) {
    return uploadFileByMultipart({
      file,
      multipart,
      cancelToken,
      onUploadProgress,
    });
  }

  let lastError;
  let expiredSignatureRetries = 0;

  // Only the give-up paths report. An intermediate retry is not a failure —
  // reporting those would turn one bad connection into three reports and make
  // the failure rate unreadable.
  //
  // Guarded even though reportUploadFailure swallows its own errors: this runs
  // immediately before the rethrow, so anything escaping here would replace the
  // upload's real error with the reporter's and the caller would be told the
  // wrong thing. A diagnostic must never rewrite what it observes.
  const reportGaveUp = (error, attempt) => {
    try {
      reportUploadFailure({
        stage: 'upload',
        error,
        file,
        attempt,
        maxAttempts,
        projectId: context.projectId,
        uploadKind: context.uploadKind,
      });
    } catch (e) {
      // Nothing to do: the upload's own error is what matters.
    }
  };

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    let signedUrl;

    try {
      signedUrl = await getSignedUrl();

      await axios.put(signedUrl, file, {
        cancelToken,
        headers: { 'Content-Type': file.type },
        onUploadProgress,
      });

      return;
    } catch (error) {
      lastError = error;

      // A cancel is the user's decision, not a failure: nothing to report.
      if (axios.isCancel(error)) throw new Error('Upload canceled');
      if (
        attempt === maxAttempts ||
        !isRetryable(error, signedUrl, expiredSignatureRetries)
      ) {
        reportGaveUp(error, attempt);
        throw error;
      }

      if (isExpiredSignature(error, signedUrl)) expiredSignatureRetries += 1;

      console.warn(
        `Upload of ${file.name} failed (attempt ${attempt}/${maxAttempts}), retrying`,
        error
      );

      await wait(BASE_BACKOFF_MS * Math.pow(2, attempt - 1));

      // Backing off is not free: cancelling during the wait has to stop the
      // next attempt rather than start one the user has already dismissed.
      if (cancelToken && cancelToken.reason) {
        throw new Error('Upload canceled');
      }
    }
  }

  // Unreachable in practice — the loop's own give-up branch throws first — but
  // it is still a give-up, so it reports like one rather than silently.
  reportGaveUp(lastError, maxAttempts);
  throw lastError;
};

export default uploadFileBySignedUrl;
